import { useParams, Link } from 'react-router-dom';
import { services } from '@/mocks/tmtData';

const serviceBenefits: Record<string, string[]> = {
  'money-transfer': [
    '14+ corridors across Middle East, Africa, and Europe',
    'Real-time interbank FX rates locked before you send',
    'Multi-rail infrastructure — bank, wallet, or cash pickup',
    'End-to-end tracking with instant delivery confirmation',
    '256-bit AES military-grade encryption on every transfer',
  ],
  'p2p-payroll': [
    'Send money with just a phone number or QR code',
    'Bulk salary disbursements with automated tax reporting',
    'Itemized remittance records for every employee',
    'Scheduled execution with configurable payout calendars',
    'Zero-error precision across up to 500+ recipients',
  ],
  'visa-cards': [
    'Virtual card issued in under 60 seconds',
    'Real-time spend controls — limits, categories, freeze/unfreeze',
    'Premium physical card with worldwide LoungeKey access (1,300+ lounges)',
    'Apple Pay and Google Pay compatible',
    'Instant push notifications for every transaction',
  ],
  'bills-merchant': [
    'One-tap checkout and auto-pay scheduling',
    'Sub-3-second e-commerce checkout flow',
    'QR-based POS payments with zero hardware',
    'Daily settlement reports and refund management',
    'Supports local cards, mobile wallets, and bank redirects',
  ],
  'budgeting': [
    'Automatic transaction categorization into budget envelopes',
    'Monthly spending caps with real-time progress visualization',
    'Linked sub-accounts for family members with configurable allowances',
    'Spending approval workflows for dependent accounts',
    'Unified family dashboard with full financial visibility',
  ],
  'reports': [
    'Live dashboards updated with every transaction',
    'Granular filters — date, category, currency, beneficiary, tags',
    'Export to PDF, CSV, or formatted print-ready documents',
    'Advanced cash-flow waterfall charts and revenue projections',
    'Expense heat maps and counterparty concentration analysis',
  ],
  'ai-insights': [
    '30-day proactive cash-flow forecasts',
    'Automated savings recommendations based on spending rhythm',
    'Early-warning alerts for unusual activity patterns',
    'Personalized product and currency conversion suggestions',
    'Self-improving models that sharpen with every interaction',
  ],
  'scan-pay': [
    'Payment completes in under two seconds',
    'Compatible with EMVCo, UnionPay QR, and GCC-Net standards',
    'Instant merchant settlement with no chargeback risk',
    'Rich transaction feed with merchant details and location tagging',
    'Automatic spending categorization for effortless expense tracking',
  ],
  'vouchers': [
    'Hundreds of partner brands across fashion, dining, travel, and entertainment',
    'Customizable TMT universal vouchers redeemable at any participating merchant',
    'Scheduled delivery with personalized messages and animated reveals',
    'Bulk corporate gifting with detailed redemption analytics',
    'Budget tracking and employee reward distribution at scale',
  ],
  'multi-currency': [
    'Up to 25 currencies with independent balances and IBANs',
    'Live interbank rates updated every 60 seconds',
    'Zero hidden markup with transparent flat conversion fee',
    'Rate alerts to automate conversions at your target rate',
    'Pay in local currency to avoid dynamic conversion surcharges',
  ],
};

export default function ServiceDetailPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const service = services.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 border border-primary-700/30 rounded-full flex items-center justify-center">
            <i className="ri-error-warning-line text-primary-400 text-3xl" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-100 mb-3">Service Not Found</h1>
          <p className="text-foreground-400 mb-6 font-body">The service you are looking for does not exist or has been moved.</p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Back to All Services
          </Link>
        </div>
      </div>
    );
  }

  const benefits = serviceBenefits[service.id] || [];

  return (
    <div className="bg-background-50">
      {/* Breadcrumb */}
      <section className="pt-24 md:pt-32 pb-0 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs font-body text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-400 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-[0.6rem]" />
            <Link to="/services" className="hover:text-primary-400 transition-colors">Services</Link>
            <i className="ri-arrow-right-s-line text-[0.6rem]" />
            <span className="text-primary-400">{service.title}</span>
          </nav>
        </div>
      </section>

      {/* Hero */}
      <section className="pt-4 pb-16 md:pb-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            {/* Text Side */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 border border-primary-500/20 rounded-full mb-5">
                <i className={`${service.icon} text-primary-400 text-sm`} />
                <span className="font-label text-[0.65rem] tracking-[0.15em] uppercase text-primary-400">
                  TMT Service
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-50 leading-tight mb-6">
                {service.title}
              </h1>
              <p className="text-foreground-300 text-base md:text-lg leading-relaxed font-body">
                {service.description}
              </p>
            </div>
            {/* Image Side */}
            <div className="relative overflow-hidden rounded-2xl border border-primary-700/20">
              <div className="absolute inset-0 bg-gradient-to-t from-background-50/30 via-transparent to-transparent z-10" />
              <img
                src={service.image}
                alt={service.title}
                className="w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Key Benefits
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              Why Choose This Service
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-5 bg-background-50 border border-primary-800/20 rounded-xl"
              >
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-primary-500/10 border border-primary-500/20 rounded-full mt-0.5">
                  <i className="ri-check-line text-primary-400 text-sm" />
                </div>
                <p className="text-foreground-200 text-sm leading-relaxed font-body">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Getting Started
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <div className="relative text-center">
              <div className="hidden md:block absolute top-11 left-[60%] w-[80%] h-px bg-gradient-to-r from-primary-700/40 to-transparent" />
              <div className="w-22 h-22 mx-auto mb-5 bg-background-100 border border-primary-700/30 rounded-full flex items-center justify-center relative">
                <i className="ri-user-add-line text-primary-400 text-2xl" />
                <span className="absolute -top-1.5 -right-1.5 w-7 h-7 bg-primary-500 text-background-50 rounded-full flex items-center justify-center font-label text-xs font-bold">
                  01
                </span>
              </div>
              <h3 className="font-heading text-lg text-foreground-100 mb-2">Sign Up & Verify</h3>
              <p className="text-foreground-400 text-sm leading-relaxed max-w-xs mx-auto font-body">
                Create your TMT account in minutes and complete our AI-powered identity verification process.
              </p>
            </div>
            <div className="relative text-center">
              <div className="hidden md:block absolute top-11 left-[60%] w-[80%] h-px bg-gradient-to-r from-primary-700/40 to-transparent" />
              <div className="w-22 h-22 mx-auto mb-5 bg-background-100 border border-primary-700/30 rounded-full flex items-center justify-center relative">
                <i className="ri-settings-3-line text-primary-400 text-2xl" />
                <span className="absolute -top-1.5 -right-1.5 w-7 h-7 bg-primary-500 text-background-50 rounded-full flex items-center justify-center font-label text-xs font-bold">
                  02
                </span>
              </div>
              <h3 className="font-heading text-lg text-foreground-100 mb-2">Configure & Fund</h3>
              <p className="text-foreground-400 text-sm leading-relaxed max-w-xs mx-auto font-body">
                Link your preferred funding source and configure the service to match your needs.
              </p>
            </div>
            <div className="relative text-center">
              <div className="w-22 h-22 mx-auto mb-5 bg-background-100 border border-primary-700/30 rounded-full flex items-center justify-center relative">
                <i className="ri-rocket-line text-primary-400 text-2xl" />
                <span className="absolute -top-1.5 -right-1.5 w-7 h-7 bg-primary-500 text-background-50 rounded-full flex items-center justify-center font-label text-xs font-bold">
                  03
                </span>
              </div>
              <h3 className="font-heading text-lg text-foreground-100 mb-2">Start Using</h3>
              <p className="text-foreground-400 text-sm leading-relaxed max-w-xs mx-auto font-body">
                You are all set — start using the service immediately with full access to all features.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-4">
            Ready to get started with{' '}
            <span className="text-primary-400">{service.title}</span>?
          </h2>
          <p className="text-foreground-300 text-base leading-relaxed mb-8 font-body">
            Join thousands of users who trust TMT for their daily financial needs. Open an account today and start enjoying seamless digital finance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              Open Free Account
            </Link>
            <Link
              to="/services"
              className="px-8 py-3 border border-primary-700/40 text-primary-400 rounded-full font-label text-sm tracking-wider hover:border-primary-400 hover:bg-primary-500/10 transition-all whitespace-nowrap"
            >
              Explore All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}